// 问卷步骤2：财务状况评估
// 包含资产负债率、净利润、现金流状况、违约记录等

import React from 'react';
import {
  Form,
  Select,
  Radio,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Alert,
} from 'antd';
import {
  DollarOutlined,
  RiseOutlined,
  SafetyOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  DebtRatio,
  ProfitScale,
  CashFlowStatus,
  DefaultRecord,
  InitialScreeningQuestionnaire,
} from '../../../types/questionnaire';

const { Title, Text } = Typography;
const { Option } = Select;

interface QuestionnaireStep2Props {
  form: any;
  questionnaire: Partial<InitialScreeningQuestionnaire>;
  onUpdate: (data: Partial<InitialScreeningQuestionnaire>) => void;
}

// 选项标签映射 - 使用翻译函数
const getDebtRatioLabels = (t: (key: string) => string) => ({
  [DebtRatio.BELOW_60]: t('questionnaire:options.debtRatio.below_60'),
  [DebtRatio.FROM_60_TO_70]: t('questionnaire:options.debtRatio.from_60_to_70'),
  [DebtRatio.FROM_70_TO_80]: t('questionnaire:options.debtRatio.from_70_to_80'),
  [DebtRatio.ABOVE_80]: t('questionnaire:options.debtRatio.above_80'),
  [DebtRatio.UNKNOWN]: t('questionnaire:options.debtRatio.unknown'),
});

const getProfitScaleLabels = (t: (key: string) => string) => ({
  [ProfitScale.ABOVE_1B]: t('questionnaire:options.profitScale.above_1b'),
  [ProfitScale.FROM_500M_TO_1B]: t(
    'questionnaire:options.profitScale.from_500m_to_1b'
  ),
  [ProfitScale.FROM_100M_TO_500M]: t(
    'questionnaire:options.profitScale.from_100m_to_500m'
  ),
  [ProfitScale.FROM_0_TO_100M]: t(
    'questionnaire:options.profitScale.from_0_to_100m'
  ),
  [ProfitScale.LOSS]: t('questionnaire:options.profitScale.loss'),
});

const getCashFlowStatusLabels = (t: (key: string) => string) => ({
  [CashFlowStatus.EXCELLENT]: t(
    'questionnaire:options.cashFlowStatus.excellent'
  ),
  [CashFlowStatus.GOOD]: t('questionnaire:options.cashFlowStatus.good'),
  [CashFlowStatus.FAIR]: t('questionnaire:options.cashFlowStatus.fair'),
  [CashFlowStatus.POOR]: t('questionnaire:options.cashFlowStatus.poor'),
  [CashFlowStatus.UNKNOWN]: t('questionnaire:options.cashFlowStatus.unknown'),
});

const getDefaultRecordLabels = (t: (key: string) => string) => ({
  [DefaultRecord.NO_DEFAULT]: t(
    'questionnaire:options.defaultRecord.no_default'
  ),
  [DefaultRecord.MINOR_RESOLVED]: t(
    'questionnaire:options.defaultRecord.minor_resolved'
  ),
  [DefaultRecord.DEFAULT_RESTRUCTURED]: t(
    'questionnaire:options.defaultRecord.default_restructured'
  ),
  [DefaultRecord.CURRENT_DEFAULT]: t(
    'questionnaire:options.defaultRecord.current_default'
  ),
});

// 风险提示配置
const getRiskAlert = (
  value: string,
  field: string,
  t: (key: string) => string
) => {
  const highRiskConditions: Record<string, string[]> = {
    debtToAssetRatio: [DebtRatio.ABOVE_80],
    netProfit: [ProfitScale.LOSS],
    cashFlowStatus: [CashFlowStatus.POOR],
    defaultRecord: [DefaultRecord.CURRENT_DEFAULT],
  };

  const mediumRiskConditions: Record<string, string[]> = {
    debtToAssetRatio: [DebtRatio.FROM_70_TO_80],
    netProfit: [ProfitScale.FROM_0_TO_100M],
    cashFlowStatus: [CashFlowStatus.FAIR],
    defaultRecord: [DefaultRecord.DEFAULT_RESTRUCTURED],
  };

  if (highRiskConditions[field]?.includes(value)) {
    return {
      type: 'error' as const,
      message: t('questionnaire:step2.riskAlertHigh'),
    };
  }

  if (mediumRiskConditions[field]?.includes(value)) {
    return {
      type: 'warning' as const,
      message: t('questionnaire:step2.riskAlertMedium'),
    };
  }

  return null;
};

export const QuestionnaireStep2: React.FC<QuestionnaireStep2Props> = ({
  form,
  questionnaire,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const watchedValues = Form.useWatch([], form) as any;

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      {/* 财务健康度总览 */}
      <Alert
        message={t('questionnaire:step2.financialDesc')}
        description={t('questionnaire:step2.financialDescContent')}
        type="info"
        showIcon
        style={{
          borderRadius: '8px',
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.05)',
        }}
      />

      {/* B1. 资产负债情况 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <DollarOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:step2.debtSection')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Row gutter={[16, 24]}>
          <Col xs={24} md={12}>
            <Form.Item
              name={['financialStatus', 'debtToAssetRatio']}
              label={t('questionnaire:step2.debtRatio')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step2.debtRatioRequired'),
                },
              ]}
              extra={t('questionnaire:step2.debtRatioExtra')}
            >
              <Select
                placeholder={t('questionnaire:step2.debtRatioPlaceholder')}
                onChange={value => {
                  const alert = getRiskAlert(value, 'debtToAssetRatio', t);
                  // 可以在这里设置风险提示状态
                }}
              >
                {Object.entries(getDebtRatioLabels(t)).map(([value, label]) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* 动态风险提示 */}
            {(() => {
              const value = watchedValues?.financialStatus?.debtToAssetRatio;
              const alert = value
                ? getRiskAlert(value, 'debtToAssetRatio', t)
                : null;
              return (
                alert && (
                  <Alert
                    message={alert.message}
                    type={alert.type}
                    showIcon
                    style={{ marginTop: '8px' }}
                  />
                )
              );
            })()}
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['financialStatus', 'netProfit']}
              label={t('questionnaire:step2.netProfit')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step2.netProfitRequired'),
                },
              ]}
              extra={t('questionnaire:step2.netProfitExtra')}
            >
              <Select
                placeholder={t('questionnaire:step2.netProfitPlaceholder')}
                onChange={value => {
                  const alert = getRiskAlert(value, 'netProfit', t);
                  // 可以在这里设置风险提示状态
                }}
              >
                {Object.entries(getProfitScaleLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>

            {/* 动态风险提示 */}
            {(() => {
              const value = watchedValues?.financialStatus?.netProfit;
              const alert = value ? getRiskAlert(value, 'netProfit', t) : null;
              return (
                alert && (
                  <Alert
                    message={alert.message}
                    type={alert.type}
                    showIcon
                    style={{ marginTop: '8px' }}
                  />
                )
              );
            })()}
          </Col>
        </Row>
      </Card>

      {/* B2. 现金流状况 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <RiseOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:step2.cashFlowSection')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Form.Item
          name={['financialStatus', 'cashFlowStatus']}
          label={t('questionnaire:step2.cashFlowStatus')}
          rules={[
            {
              required: true,
              message: t('questionnaire:step2.cashFlowStatusRequired'),
            },
          ]}
          extra={t('questionnaire:step2.cashFlowStatusExtra')}
        >
          <Radio.Group
            style={{ width: '100%' }}
            onChange={e => {
              const alert = getRiskAlert(e.target.value, 'cashFlowStatus', t);
              // 可以在这里设置风险提示状态
            }}
          >
            <Row gutter={[16, 16]}>
              {Object.entries(getCashFlowStatusLabels(t)).map(
                ([value, label]) => (
                  <Col xs={24} sm={12} key={value}>
                    <Radio
                      value={value}
                      style={{ width: '100%', padding: '8px' }}
                    >
                      {label}
                    </Radio>
                  </Col>
                )
              )}
            </Row>
          </Radio.Group>
        </Form.Item>

        {/* 动态风险提示 */}
        {(() => {
          const value = watchedValues?.financialStatus?.cashFlowStatus;
          const alert = value ? getRiskAlert(value, 'cashFlowStatus', t) : null;
          return (
            alert && (
              <Alert
                message={alert.message}
                type={alert.type}
                showIcon
                style={{ marginTop: '12px' }}
              />
            )
          );
        })()}
      </Card>

      {/* B3. 信用记录 */}
      <Card
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <SafetyOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:step2.defaultSection')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Form.Item
          name={['financialStatus', 'defaultRecord']}
          label={t('questionnaire:step2.defaultRecord')}
          rules={[
            {
              required: true,
              message: t('questionnaire:step2.defaultRecordRequired'),
            },
          ]}
          extra={t('questionnaire:step2.defaultRecordExtra')}
        >
          <Radio.Group
            style={{ width: '100%' }}
            onChange={e => {
              const alert = getRiskAlert(e.target.value, 'defaultRecord', t);
              // 可以在这里设置风险提示状态
            }}
          >
            <Row gutter={[16, 16]}>
              {Object.entries(getDefaultRecordLabels(t)).map(
                ([value, label]) => (
                  <Col xs={24} sm={12} key={value}>
                    <Radio
                      value={value}
                      style={{ width: '100%', padding: '8px' }}
                    >
                      {label}
                    </Radio>
                  </Col>
                )
              )}
            </Row>
          </Radio.Group>
        </Form.Item>

        {/* 动态风险提示 */}
        {(() => {
          const value = watchedValues?.financialStatus?.defaultRecord;
          const alert = value ? getRiskAlert(value, 'defaultRecord', t) : null;
          return (
            alert && (
              <Alert
                message={alert.message}
                type={alert.type}
                showIcon
                style={{ marginTop: '12px' }}
              />
            )
          );
        })()}
      </Card>

      {/* 财务健康度综合提示 */}
      <Card
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <Space>
          <ExclamationCircleOutlined style={{ color: '#10b981' }} />
          <div>
            <Text style={{ color: '#10b981' }}>
              <strong>{t('questionnaire:step2.healthAssessment')}</strong>
            </Text>
            <div style={{ marginTop: '4px', color: '#6b7280' }}>
              • {t('questionnaire:step2.healthTip1')}
              <br />• {t('questionnaire:step2.healthTip2')}
              <br />• {t('questionnaire:step2.healthTip3')}
            </div>
          </div>
        </Space>
      </Card>
    </Space>
  );
};
